import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
import { SharedAlert } from "../../app/siteSlice";
import { AlertLevel } from "../../components/AlertMessage";
import ButtonWithForm from "../../components/ButtonWithForm";
import SimplePagination from "../../components/SimplePagination";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { Loading } from "../../components/Loading";
import SearchFilter from "../../components/input/SearchFilter";
import Select from "../../components/input/Select";
import { useGetGenresQuery } from "../lookup/lookupApiSlice";
import PromptListTable from "../prompts/PromptListTable";
import {
	PromptListQueryModel,
	PromptType,
	useGetPromptListQuery,
	useManagePromptsQuery,
	useReactivateCoursePromptMutation,
} from "../prompts/promptsApiSlice";
import ReactivatePromptForm from "./ReactivatePromptForm";

interface Props {
	selectedPromptId?: number;
	setSelectedPromptId: (id?: number) => void;
	searchFilter: string;
	setAlert: (alert: SharedAlert) => void;
}

export default function PromptLibrary(props: Props) {
	const navigate = useNavigate();

	const _displayLength = 10;
	const initQueryargs: PromptListQueryModel = {
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "desc",
		search_Filter: "",
		level_Filter: "",
		genre_Filter: "",
		status_Filter: "",
		grade_Filter: "",
		content_Filter: "",
		plagiarism_Filter: "",
	};

	const [genreFilterOptions, setGenreFilterOptions] = useState<SimpleOption[]>([]);
	const [contentAreaFilterOptions, setContentAreaFilterOptions] = useState<SimpleOption[]>([]);
	const [isFiltered, setIsFiltered] = useState(false);

	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState(props.searchFilter);
	const [promptTypeFilter, setPromptTypeFilter] = useState("");
	const [genreFilter, setGenreFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [contentAreaFilter, setContentAreaFilter] = useState("");
	const [plagiarismFilter, setPlagiarismFilter] = useState("");
	const [reactivatePromptId, setReactivatePromptId] = useState("");

	const promptListResult = useGetPromptListQuery(queryargs);
	const prompts = promptListResult.data;

	const genresResult = useGetGenresQuery();
	const genres = genresResult.data;

	const manageResult = useManagePromptsQuery();
	const manage = manageResult.data;

	const [reactivatePrompt] = useReactivateCoursePromptMutation();

	useEffect(() => {
		setQueryargs({
			...queryargs,
			iDisplayStart: _displayLength * page,
			search_Filter: searchFilter,
			level_Filter: promptTypeFilter,
			genre_Filter: genreFilter,
			status_Filter: statusFilter,
			content_Filter: contentAreaFilter,
			plagiarism_Filter: plagiarismFilter,
		});
	}, [
		page,
		searchFilter,
		promptTypeFilter,
		genreFilter,
		statusFilter,
		contentAreaFilter,
		plagiarismFilter,
	]);

	useEffect(() => {
		setIsFiltered(
			!!searchFilter ||
				!!promptTypeFilter ||
				!!genreFilter ||
				!!statusFilter ||
				!!contentAreaFilter ||
				!!plagiarismFilter
		);
		setPage(0);
	}, [
		searchFilter,
		promptTypeFilter,
		genreFilter,
		statusFilter,
		contentAreaFilter,
		plagiarismFilter,
	]);

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	useEffect(() => {
		if (!genres) return;
		const filters = [...genres]
			.sort((a, b) => (a.name > b.name ? 1 : -1))
			.map((t) => {
				return {
					value: t.genreId.toString(),
					label: t.name,
				} as SimpleOption;
			});
		filters.unshift({ value: "", label: "All" } as SimpleOption);
		setGenreFilterOptions(filters);
	}, [genres]);

	useEffect(() => {
		if (!manage) return;
		const filters = manage.contentAreas.map((c) => {
			return {
				value: c,
				label: c,
			} as SimpleOption;
		});
		filters.unshift({ value: "", label: "All" } as SimpleOption);
		setContentAreaFilterOptions(filters);
	}, [manage]);

	function updateSelectedPrompt(id: string) {
		setReactivatePromptId(id);
	}

	function handleReactivateCoursePrompt() {
		if (reactivatePromptId) {
			reactivatePrompt(Number(reactivatePromptId))
				.unwrap()
				.then(() => {
					props.setAlert({
						level: AlertLevel.Info,
						message: `The prompt has been reactivated for this course.`,
					});
				});
		}
	}

	return (
		<section>
			<div className="flex justify-between mt-4 items-end">
				<div
					className={`grid gap-4 ${manage?.canSeePlagiarismChecker ? "md:grid-cols-5" : "md:grid-cols-4"} w-full`}>
					<Select
						value={statusFilter}
						setValue={(v) => setStatusFilter(v as string)}
						label="Status"
						simpleListBoxId="statusFilter"
						options={[
							{ value: "", label: "All" },
							{
								value: "Enabled",
								label: "Enabled",
							},
							{
								value: "Disabled",
								label: "Disabled",
							},
						]}
						simpleListBoxClasses="w-full"
					/>
					<Select
						value={promptTypeFilter}
						setValue={(v) => setPromptTypeFilter(v as string)}
						label="Prompt Type"
						simpleListBoxId="promptTypeFilter"
						options={[
							{ value: "", label: "All" },
							{
								value: PromptType.District,
								label: PromptType.District,
							},
							{
								value: "My Prompts",
								label: "My Prompts",
							},
							{
								value: "System",
								label: PromptType.System,
							},
							{
								value: PromptType.School,
								label: PromptType.School,
							},
						]}
						fieldClasses="pt-2 md:pt-0"
						simpleListBoxClasses="w-full"
					/>
					<Select
						value={genreFilter}
						setValue={(v) => setGenreFilter(v as string)}
						label="Genre"
						simpleListBoxId="genreFilter"
						options={genreFilterOptions}
						fieldClasses="pt-2 md:pt-0"
						simpleListBoxClasses="w-full"
					/>
					<Select
						value={contentAreaFilter}
						setValue={(v) => setContentAreaFilter(v as string)}
						label="Content Area"
						simpleListBoxId="contentAreaFilter"
						options={contentAreaFilterOptions}
						simpleListBoxClasses="w-full"
					/>
					{manage?.canSeePlagiarismChecker && (
						<Select
							value={plagiarismFilter}
							setValue={(v) => setPlagiarismFilter(v as string)}
							label="Plagiarism Checker"
							simpleListBoxId="plagiarismFilter"
							options={[
								{ value: "", label: "All" },
								{
									value: "On",
									label: "Available",
								},
								{
									value: "Off",
									label: "Unavailable",
								},
							]}
							simpleListBoxClasses="w-full"
						/>
					)}
				</div>
			</div>
			<div className="flex justify-between gap-4 items-end">
				<SearchFilter
					id="searchByTitleOrDescription"
					name="searchByTitleOrDescription"
					searchFilter={searchFilter}
					setSearchFilter={setSearchFilter}
					label="Search"
					placeHolder="Search by title or description..."
					className="mt-2 w-full"
					debounceDelay
				/>
				{manage && manage.inactivePrompts.length > 0 && (
					<ButtonWithForm
						id="reactivate-prompt-button"
						className="btn btn-neutral min-w-fit"
						title="Reactivate Prompt In Course"
						confirmLabel="Reactivate Prompt"
						onConfirm={handleReactivateCoursePrompt}
						form={
							<ReactivatePromptForm
								prompts={manage.inactivePrompts}
								updateSelectedPrompt={updateSelectedPrompt}
							/>
						}>
						<span className="flex items-center">
							<ArrowPathIcon className="size-4 mr-2" /> Reactivate Prompt
						</span>
					</ButtonWithForm>
				)}
				<Button
					id="create-prompt-button"
					className="btn btn-primary min-w-40 whitespace-nowrap"
					onClick={() => navigate("/writing/prompt")}>
					<span className="flex items-center">
						<PlusIcon className="size-4 mr-2" /> Create Prompt
					</span>
				</Button>
			</div>
			<Loading loading={promptListResult.isLoading} />
			{prompts && (
				<PromptListTable
					prompts={prompts.aaData}
					handlePageChange={handlePageChange}
					selectedPromptId={props.selectedPromptId}
					setSelectedPromptId={props.setSelectedPromptId}
					setAlert={props.setAlert}
				/>
			)}
			{((prompts?.iTotalDisplayRecords ?? 0) > queryargs.iDisplayLength || isFiltered) && (
				<SimplePagination
					itemCount={prompts?.iTotalDisplayRecords}
					page={page}
					pageSize={queryargs.iDisplayLength}
					totalCount={prompts?.iTotalRecords}
					isFiltered={isFiltered}
					onChange={(page) => handlePageChange(page)}
					className={"border-none"}
				/>
			)}
		</section>
	);
}
